import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { EmailSend } from '../../types/api';

const errorText = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(' ; ');
  return err?.message || 'Erreur inconnue';
};

const when = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';

export interface AskConfirm {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
}

/** Audience + send + send log of one custom email (issue #11). Send is always on the SAVED version. */
const SendPanel: React.FC<{
  eventId: number;
  emailId: number;
  subject: string;
  dirty: boolean;
  askConfirm: (c: AskConfirm) => void;
}> = ({ eventId, emailId, subject, dirty, askConfirm }) => {
  const toast = useToast();
  const qc = useQueryClient();
  const [audience, setAudience] = useState('all');
  const [vipOnly, setVipOnly] = useState(false);
  const [resend, setResend] = useState(false);

  useEffect(() => { setAudience('all'); setVipOnly(false); setResend(false); }, [emailId]);

  const audiencesQ = useQuery({
    queryKey: ['email-audiences', eventId, emailId, vipOnly],
    queryFn: () => apiService.getEmailAudiences(eventId, emailId, vipOnly),
  });
  const sendsQ = useQuery({
    queryKey: ['email-sends', eventId, emailId],
    queryFn: () => apiService.getEmailSends(eventId, emailId),
  });

  const current = audiencesQ.data?.find((a) => a.key === audience);
  const toSend = current ? (resend ? current.count : current.count - current.already_sent) : 0;

  const send = useMutation({
    mutationFn: () =>
      apiService.sendEventEmail(eventId, emailId, {
        audience, vip_only: vipOnly, resend, confirm: true, expected_count: toSend,
      }),
    onSuccess: (res: EmailSend) => {
      toast({
        title: 'Envoi terminé',
        description: `${res.sent_count} envoyé(s), ${res.failed_count} échec(s)`,
        status: res.failed_count ? 'warning' : 'success',
        duration: 8000,
      });
      setResend(false);
    },
    onError: (err) => toast({ title: 'Envoi refusé', description: errorText(err), status: 'error', duration: 10000 }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['email-audiences', eventId, emailId] });
      qc.invalidateQueries({ queryKey: ['email-sends', eventId, emailId] });
    },
  });

  const ask = () =>
    askConfirm({
      title: 'Envoyer cet e-mail maintenant ?',
      body: `${toSend} personne(s) vont recevoir l'e-mail « ${subject} » (${current?.label || ''}${vipOnly ? ', VIP uniquement' : ''}). L'envoi ne peut pas être annulé.`,
      confirmLabel: `Envoyer à ${toSend} personne(s)`,
      onConfirm: () => send.mutate(),
    });

  return (
    <Card variant="outline" data-testid="send-panel">
      <CardHeader pb={2}>
        <Heading size="sm">Envoyer</Heading>
      </CardHeader>
      <CardBody pt={0}>
        <VStack align="stretch" spacing={3}>
          {audiencesQ.isLoading && <Spinner size="sm" />}
          {audiencesQ.isError && (
            <Alert status="error" borderRadius="md"><AlertIcon />{errorText(audiencesQ.error)}</Alert>
          )}
          {audiencesQ.data && (
            <>
              <Box>
                <Text fontSize="sm" mb={1}>Destinataires</Text>
                <Select size="sm" bg="white" value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="send-audience">
                  {audiencesQ.data.map((a) => (
                    <option key={a.key} value={a.key}>
                      {a.label} ({a.count})
                    </option>
                  ))}
                </Select>
              </Box>
              <label style={{ fontSize: '0.875rem' }}>
                <input type="checkbox" checked={vipOnly} onChange={(e) => setVipOnly(e.target.checked)} data-testid="send-vip" />{' '}
                VIP uniquement
              </label>
              {current && current.already_sent > 0 && (
                <Box>
                  <Text fontSize="sm" color="orange.600" data-testid="send-already">
                    {current.already_sent} personne(s) de cette liste ont déjà reçu cet e-mail : elles seront ignorées.
                  </Text>
                  <label style={{ fontSize: '0.875rem' }}>
                    <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} data-testid="send-resend" />{' '}
                    Renvoyer aussi à ces personnes
                  </label>
                </Box>
              )}
              <Flex align="center" gap={3} wrap="wrap">
                <Button
                  colorScheme="blue"
                  size="sm"
                  onClick={ask}
                  isDisabled={dirty || toSend === 0}
                  isLoading={send.isPending}
                  title={dirty ? "Enregistrez d'abord : l'envoi utilise la version enregistrée" : undefined}
                  data-testid="send-now"
                >
                  Envoyer maintenant
                </Button>
                <Text fontSize="sm" color="gray.600" data-testid="send-count">
                  {toSend} personne(s) recevront cet e-mail
                </Text>
              </Flex>
              {dirty && <Text fontSize="xs" color="gray.500">Enregistrez vos modifications avant d'envoyer.</Text>}
            </>
          )}

          <Box>
            <Heading size="xs" mb={2}>Historique des envois</Heading>
            {sendsQ.data && sendsQ.data.length === 0 && <Text fontSize="sm" color="gray.500">Aucun envoi pour le moment.</Text>}
            <VStack align="stretch" spacing={1} data-testid="send-log">
              {(sendsQ.data || []).map((s) => (
                <Flex key={s.id} fontSize="sm" gap={2} wrap="wrap" align="center">
                  <Text>{when(s.started_at)}</Text>
                  <Text color="gray.600">{audiencesQ.data?.find((a) => a.key === s.audience)?.label || s.audience}{s.vip_only ? ' · VIP' : ''}{s.resend ? ' · renvoi' : ''}</Text>
                  <Badge colorScheme="green">{s.sent_count} envoyé(s)</Badge>
                  {s.failed_count > 0 && <Badge colorScheme="red">{s.failed_count} échec(s)</Badge>}
                </Flex>
              ))}
            </VStack>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default SendPanel;
